/**
 * Graphics preview in a sandboxed, network-less iframe using the same host
 * page as the export renderer (buildGraphicsHost). The template runs in an
 * opaque origin: it cannot read PRISM cookies, storage or APIs.
 *
 * The frame is /graphics-frame.html (0.30.0), which has a policy of its own: a
 * srcdoc frame would inherit the app's Content-Security-Policy, which forbids the
 * inline scripts templates are made of. The host document is posted to the frame,
 * which replaces itself with it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildGraphicsHost, type TemplateManifest } from '@prism/contracts';

export function GraphicPreview({ bundle, manifest, data, t, durationSeconds, className, style }: {
  bundle: string;
  manifest: TemplateManifest;
  data: Record<string, string>;
  /** Local time within the graphic, in seconds. */
  t: number;
  durationSeconds: number;
  className?: string;
  style?: React.CSSProperties;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const srcDocRef = useRef('');
  const srcDoc = useMemo(() => buildGraphicsHost(bundle), [bundle]);
  // A new document needs a new frame and a new handshake.
  const frameKey = useMemo(() => hash(srcDoc), [srcDoc]);
  useEffect(() => setReady(false), [srcDoc]);
  srcDocRef.current = srcDoc;

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / manifest.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, [manifest.width]);

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.source !== frame.current?.contentWindow) return;
      if (e.data?.type === 'prism:frame-ready') frame.current?.contentWindow?.postMessage({ type: 'prism:load', html: srcDocRef.current }, '*');
      if (e.data?.type === 'prism:ready') setReady(true);
      if (e.data?.type === 'prism:error') setError(String(e.data.message));
      if (e.data?.type === 'prism:rendered') setError(null);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready) return;
    frame.current?.contentWindow?.postMessage(
      { type: 'prism:render', id: Date.now(), t: Math.max(0, Math.min(durationSeconds, t)), data, info: { durationSeconds, width: manifest.width, height: manifest.height } },
      '*',
    );
  }, [ready, t, data, durationSeconds, manifest.width, manifest.height]);

  return (
    <div ref={box} className={className} style={{ position: 'relative', aspectRatio: `${manifest.width} / ${manifest.height}`, overflow: 'hidden', ...style }}>
      <iframe
        key={frameKey}
        ref={frame}
        title={`${manifest.name} preview`}
        // Matching colour schemes keeps the cross-origin frame transparent.
        sandbox="allow-scripts"
        src="/graphics-frame.html"
        style={{ position: 'absolute', left: 0, top: 0, width: manifest.width, height: manifest.height, border: 0, transform: `scale(${scale})`, transformOrigin: '0 0', background: 'transparent', colorScheme: 'dark', pointerEvents: 'none' }}
      />
      {error && <div className="graphic-error">{error}</div>}
    </div>
  );
}

/** A short fingerprint of the host document, to give each document its own frame. */
function hash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return `${text.length}-${h >>> 0}`;
}
